import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Text,
  Select,
  Heading,
  useToast,
  VStack,
  HStack,
  Avatar,
  IconButton,
  Divider,
  Tabs, 
  TabList, 
  TabPanels, 
  Tab, 
  TabPanel,
  Switch,
  FormHelperText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Icon,
  Tooltip,
  Flex,
  useColorModeValue,
} from "@chakra-ui/react";
import { MdUpload, MdAccessTime, MdCheckCircle, MdCancel, MdStorefront, MdPeople, MdStar, MdStars, MdInfo, MdLock, MdSchedule } from "react-icons/md";
import { RiInstagramFill, RiFacebookFill, RiTwitterXFill, RiLinkedinFill } from "react-icons/ri";
import Card from "../../../components/card/Card";
import { useState, useEffect, useRef } from "react";
import { restaurantService } from "../../../services/restaurantService";
import { pricingService } from "../../../services/pricingService";
import { paymentService } from "../../../services/paymentService";
import PremiumUpgradeModal from "../../../components/modals/PremiumUpgradeModal";
import { keyframes } from "@emotion/react";
import { MdCreditCard } from "react-icons/md";
import { ManagerOnly } from "../../../components/RoleGuard";
import { authService } from "../../../services/authService";
import {
  cloudinaryDeliveryUrl,
  coerceCloudinaryAbsoluteIfPartial,
} from "../../../utils/cloudinaryDeliveryUrl";

// Logo URL'ini oluştur - dosya adını encode et; Cloudinary ise f_auto,q_auto + kare küçültme
const getLogoUrl = (logoPath) => {
  if (!logoPath) return undefined;
  const partialCloud = coerceCloudinaryAbsoluteIfPartial(logoPath);
  if (partialCloud) {
    return cloudinaryDeliveryUrl(partialCloud, 'square500');
  }
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return cloudinaryDeliveryUrl(logoPath, 'square500');
  }
  // API URL'i al (fallback ile)
  let apiUrl = import.meta.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
  
  // Eğer URL'de protocol yoksa, production'da https ekle
  if (apiUrl && !apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
    // Production'da https kullan, development'ta http
    apiUrl = (process.env.NODE_ENV === 'production' ? 'https://' : 'http://') + apiUrl;
  }
  
  // Path'i encode et - sadece dosya adını encode et, path'i değil
  const pathParts = logoPath.split('/');
  const fileName = pathParts.pop();
  const encodedPath = pathParts.join('/') + '/' + encodeURIComponent(fileName);
  return cloudinaryDeliveryUrl(`${apiUrl}${encodedPath}`, 'square500');
};

/** Çalışma saatleri: API / hatalı state'teki fazladan anahtarları temizler; <input type="time"> için HH:mm */
const WORKING_HOURS_DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DEFAULT_DAY_HOURS = { open: "09:00", close: "22:00", isOpen: true };

function normalizeTimeForInput(v, fallback) {
  if (v == null || String(v).trim() === "") return fallback;
  const s = String(v).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!m) return fallback;
  const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

function sanitizeWorkingHours(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const out = {};
  for (const day of WORKING_HOURS_DAY_KEYS) {
    const piece = src[day] && typeof src[day] === "object" ? src[day] : {};
    const isOpen =
      typeof piece.isOpen === "boolean" ? piece.isOpen : DEFAULT_DAY_HOURS.isOpen;
    if (!isOpen) {
      out[day] = {
        isOpen: false,
        open: piece.open != null ? String(piece.open) : "",
        close: piece.close != null ? String(piece.close) : "",
      };
    } else {
      out[day] = {
        isOpen: true,
        open: normalizeTimeForInput(piece.open, DEFAULT_DAY_HOURS.open),
        close: normalizeTimeForInput(piece.close, DEFAULT_DAY_HOURS.close),
      };
    }
  }
  return out;
}

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const PlanCard = ({ plan, onUpgrade }) => {
  const isPremium = plan === 'PREMIUM';
  
  return (
    <VStack 
      align="center" 
      p={4} 
      borderRadius="xl" 
      position="relative"
      overflow="hidden"
      bg="white"
      boxShadow="xl"
      {...(isPremium && {
        _before: {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, #FFD700, #FFA500, #FF8C00, #FFD700)',
          backgroundSize: '200% 200%',
          animation: `${gradientAnimation} 3s ease infinite`,
          opacity: 0.15,
          zIndex: 0,
        }
      })}
    >
      <Icon 
        as={MdStars} 
        color={isPremium ? 'yellow.400' : 'gray.400'} 
        boxSize={8} 
        zIndex={1}
      />
      <Text 
        fontSize="lg" 
        fontWeight="bold"
        color={isPremium ? 'yellow.600' : 'gray.600'}
        zIndex={1}
      >
        {isPremium ? 'Premium Paket' : 'Standart Paket'}
      </Text>
      
      {!isPremium && (
        <VStack spacing={2} mt={2} zIndex={1}>
          <Text fontSize="sm" color="gray.500" textAlign="center">
            Premium pakete geçerek tüm özelliklere erişin
          </Text>
          <Tooltip 
            label="Premium'a yükseltmek için tıklayın" 
            placement="top"
          >
            <Button
              size="sm"
              colorScheme="yellow"
              variant="solid"
              leftIcon={<MdStars />}
              onClick={onUpgrade}
            >
              Premium'a Yükselt
            </Button>
          </Tooltip>
        </VStack>
      )}

      {isPremium && (
        <HStack spacing={1} mt={2} zIndex={1}>
          <Icon as={MdCheckCircle} color="green.500" />
          <Text fontSize="sm" color="green.600">
            Tüm özellikler aktif
          </Text>
        </HStack>
      )}
    </VStack>
  );
};

export default function Settings() {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  
  // Role kontrolü
  const currentUser = authService.getCurrentUser();
  const isStaff = currentUser?.role === 'staff' && currentUser?.userType === 'employee';
  const isManager = !isStaff; // Manager veya business user
  
  // Color mode values
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = useColorModeValue("gray.400", "gray.300");
  const inputBg = useColorModeValue("white", "navy.700");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.50", "navy.800");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const [cardStatus, setCardStatus] = useState(null);
  const [isLoadingCardStatus, setIsLoadingCardStatus] = useState(false);
  const [cardFormData, setCardFormData] = useState({
    cardNumber: '',
    cardHolderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const [isSavingCard, setIsSavingCard] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    logo: '',
    address: {
      street: '',
      district: '',
      city: '',
      fullAddress: ''
    },
    workingHours: {
      monday: { open: '09:00', close: '22:00', isOpen: true },
      tuesday: { open: '09:00', close: '22:00', isOpen: true },
      wednesday: { open: '09:00', close: '22:00', isOpen: true },
      thursday: { open: '09:00', close: '22:00', isOpen: true },
      friday: { open: '09:00', close: '22:00', isOpen: true },
      saturday: { open: '09:00', close: '22:00', isOpen: true },
      sunday: { open: '09:00', close: '22:00', isOpen: true }
    },
    features: {
      wifi: false,
      parking: false,
      terrace: false,
      seaView: false,
      liveMusic: false,
      noSmoking: false,
      accessible: false,
      buffet: false,
      breakfast: false,
      openAfterMidnight: false,
      kidsPlayground: false,
      kidsTable: false,
      takeaway: false,
      outdoorSeating: false,
      petFriendly: false,
      reservations: false,
      privateRoom: false,
      valetParking: false,
      airConditioning: false,
      heating: false,
    },
    socialMedia: {
      instagram: '',
      facebook: '',
      twitter: '',
      linkedin: ''
    },
    website: '',
    plan: 'STANDARD'
  });

  useEffect(() => {
    fetchRestaurantDetails();
    fetchCardStatus();
  }, []);

  const fetchCardStatus = async () => {
    setIsLoadingCardStatus(true);
    try {
      const response = await paymentService.getCurrentMonthPaymentStatus();
      if (response.success && response.data.cardStatus) {
        setCardStatus(response.data.cardStatus);
      }
    } catch (error) {
      console.error('Kart durumu alınırken hata:', error);
    } finally {
      setIsLoadingCardStatus(false);
    }
  };

  const handleCardSave = async () => {
    if (!cardFormData.cardNumber || !cardFormData.cardHolderName || !cardFormData.expiryMonth || !cardFormData.expiryYear || !cardFormData.cvv) {
      toast({
        title: 'Eksik Bilgi',
        description: 'Lütfen tüm kart bilgilerini doldurun',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSavingCard(true);
    try {
      const response = await paymentService.savePaymentCard(cardFormData);
      if (response.success) {
        toast({
          title: 'Başarılı',
          description: 'Kart bilgileriniz başarıyla kaydedildi',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await fetchCardStatus();
        setCardFormData({
          cardNumber: '',
          cardHolderName: '',
          expiryMonth: '',
          expiryYear: '',
          cvv: ''
        });
      }
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Kart kaydedilirken bir hata oluştu',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingCard(false);
    }
  };

  const fetchRestaurantDetails = async () => {
    try {
      const response = await restaurantService.getRestaurantDetails();
      if (response.success) {
        setFormData(prevData => ({
          ...prevData,
          ...response.data,
          logo: response.data.logo || '',
          email: response.data.email || '', // Email'i açıkça set et
          address: {
            ...prevData.address,
            ...(response.data.address || {})
          },
          workingHours: sanitizeWorkingHours({
            ...prevData.workingHours,
            ...(response.data.workingHours || {}),
          }),
          features: {
            ...prevData.features,
            ...(response.data.features || {})
          },
          socialMedia: {
            ...prevData.socialMedia,
            ...(response.data.socialMedia || {})
          },
          website: response.data.website || '',
          plan: response.data.plan || 'STANDARD'
        }));
      }
    } catch (error) {
      console.error('Restaurant detayları alınırken hata:', error);
      toast({
        title: 'Hata',
        description: 'Bilgiler alınırken bir hata oluştu',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith("workingHours.")) {
      const parts = name.split(".");
      if (parts.length === 3 && parts[0] === "workingHours") {
        const day = parts[1];
        const field = parts[2];
        if (!WORKING_HOURS_DAY_KEYS.includes(day)) return;
        if (!["open", "close", "isOpen"].includes(field)) return;
        setFormData((prev) => ({
          ...prev,
          workingHours: {
            ...prev.workingHours,
            [day]: {
              ...(prev.workingHours[day] || { ...DEFAULT_DAY_HOURS }),
              [field]:
                field === "isOpen"
                  ? value === true || value === "true"
                  : value,
            },
          },
        }));
      }
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleWorkingHoursToggle = (day, isOpen) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          isOpen: isOpen,
          open: isOpen ? '09:00' : '',
          close: isOpen ? '22:00' : ''
        }
      }
    }));
  };

  const handleImageUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      // Dosya tipini kontrol et
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Hata',
          description: 'Lütfen geçerli bir resim dosyası seçin',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Dosya boyutunu kontrol et (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Hata',
          description: 'Resim dosyası 5MB\'dan küçük olmalıdır',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      const response = await restaurantService.uploadProfileImage(formData);

      if (response.success) {
        setFormData(prev => ({
          ...prev,
          logo: response.data.imageUrl
        }));

        toast({
          title: 'Başarılı',
          description: 'Logo başarıyla güncellendi',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Resim yükleme hatası:', error);
      toast({
        title: 'Hata',
        description: 'Logo yüklenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        workingHours: sanitizeWorkingHours(formData.workingHours),
      };
      const response = await restaurantService.updateRestaurantDetails(payload);
      if (response.success) {
        toast({
          title: 'Başarılı',
          description: 'Bilgileriniz güncellendi',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast({
        title: 'Hata',
        description: 'Bilgiler güncellenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Premium özellikleri
  const premiumFeatures = [
    "📊 Gelişmiş Gelir & Sipariş Analizi",
    "📦 Akıllı Stok Yönetimi",
    "🪑 Masa & Görev Analizi",
    "🍽️ Menü Performans Analizi",
    "⭐ Müşteri Değerlendirmeleri",
    "🎯 Kendi Kampanyalarınızı Oluşturun",
    "👥 Çalışan Yönetimi",
    "🚀 Öncelikli 7/24 Destek",
  ];

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card mb="20px">
        <VStack spacing={6} align="stretch">
          {/* Profil Resmi ve İsim Bölümü */}
          <HStack 
            spacing={{ base: 3, md: 6 }} 
            align="start" 
            p={{ base: 4, md: 6 }} 
            bg='linear-gradient(135deg, #545454 20%, #E45858 100%)' 
            borderRadius='16px'
            flexDirection={{ base: "column", md: "row" }}
          >
            <Box position="relative" alignSelf={{ base: "center", md: "start" }}>
              <Avatar
                size={{ base: "xl", md: "2xl" }}
                src={getLogoUrl(formData.logo)}
                name={formData.name}
                bg="white"
                borderRadius="16px"
                border="4px solid"
                borderColor="#E45858"
                w={{ base: "120px", md: "150px" }}
                h={{ base: "120px", md: "150px" }}
              />
              <Tooltip 
                label={
                  isStaff ? (
                    <Text>Fotoğraf yüklemek için yönetici yetkisine ihtiyacınız var</Text>
                  ) : (
                    <VStack spacing={1} p={2}>
                      <Text fontWeight="bold">Logo Gereksinimleri:</Text>
                      <Text>• Önerilen boyut: 400x400 piksel</Text>
                      <Text>• Minimum: 200x200 piksel</Text>
                      <Text>• Maksimum: 800x800 piksel</Text>
                      <Text>• Format: JPG, PNG</Text>
                      <Text>• Maksimum boyut: 5MB</Text>
                    </VStack>
                  )
                }
                placement="right"
                hasArrow
              >
                <IconButton
                  icon={<MdUpload />}
                  isRound
                  size={{ base: "xs", md: "sm" }}
                  position="absolute"
                  bottom={2}
                  right={2}
                  border="2px solid"
                  borderColor="#E45858"
                  bg="white"
                  color="#E45858"
                  _hover={{ bg: isStaff ? 'white' : 'gray.100' }}
                  onClick={() => !isStaff && fileInputRef.current.click()}
                  isDisabled={isStaff}
                  cursor={isStaff ? 'not-allowed' : 'pointer'}
                />
              </Tooltip>
              <Input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                display="none"
                onChange={handleImageUpload}
                disabled={isStaff}
              />
            </Box>
            
            <VStack 
              align={{ base: "center", md: "start" }} 
              spacing={2} 
              flex={1}
              textAlign={{ base: "center", md: "left" }}
            >
              <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="white">
                {formData.name || 'İşletme Adı'}
              </Text>
              <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>
                {formData.address.district}, {formData.address.city}
              </Text>
            </VStack>

            <HStack 
              spacing={{ base: 2, md: 4 }} 
              justify="center" 
              w={{ base: "100%", md: "40%" }}
              flexWrap="wrap"
            >
              <VStack 
                align="center" 
                bg="whiteAlpha.200" 
                p={{ base: 1.5, md: 2 }} 
                borderRadius="lg" 
                flex={{ base: "1 1 calc(50% - 8px)", md: 1 }}
                minW={{ base: "calc(50% - 8px)", md: "auto" }}
                h={{ base: "80px", md: "100px" }}
                justify="center"
              >
                <Icon as={MdStorefront} color="white" boxSize={{ base: 5, md: 6 }} />
                <Text color="white" fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" textAlign="center">
                  {formData.businessType === 'RESTAURANT' && 'Restaurant'}
                  {formData.businessType === 'CAFE' && 'Kafe'}
                  {formData.businessType === 'FAST_FOOD' && 'Fast Food'}
                  {formData.businessType === 'FINE_DINING' && 'Fine Dining'}
                  {formData.businessType === 'BISTRO' && 'Bistro'}
                </Text>
              </VStack>

              <VStack 
                align="center" 
                bg="whiteAlpha.200" 
                p={{ base: 1.5, md: 2 }} 
                borderRadius="lg" 
                flex={{ base: "1 1 calc(50% - 8px)", md: 1 }}
                minW={{ base: "calc(50% - 8px)", md: "auto" }}
                h={{ base: "80px", md: "100px" }}
                justify="center"
              >
                <Icon as={MdPeople} color="white" boxSize={{ base: 5, md: 6 }} />
                <Text color="white" fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
                  {formData.followers?.length || 0}
                </Text>
                <Text color="gray.300" fontSize={{ base: "2xs", md: "xs" }}>
                  Takipçi
                </Text>
              </VStack>

              <VStack 
                align="center" 
                bg="whiteAlpha.200" 
                p={{ base: 1.5, md: 2 }} 
                borderRadius="lg" 
                flex={{ base: "1 1 calc(50% - 8px)", md: 1 }}
                minW={{ base: "calc(50% - 8px)", md: "auto" }}
                h={{ base: "80px", md: "100px" }}
                justify="center"
              >
                <HStack spacing={1} align="center">
                  <Icon as={MdStar} color="yellow.400" boxSize={{ base: 5, md: 6 }} />
                  <Text color="white" fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
                    {formData.rating?.averageRating?.toFixed(1) || '0.0'}
                  </Text>
                </HStack>
                <Text color="white" fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
                  {formData.rating?.totalRatings || 0}
                </Text>
                <Text color="gray.300" fontSize={{ base: "2xs", md: "xs" }}>
                  Değerlendirme
                </Text>
              </VStack>

              <VStack 
                align="center" 
                bg="whiteAlpha.200" 
                p={{ base: 1.5, md: 2 }} 
                borderRadius="lg" 
                flex={{ base: "1 1 calc(50% - 8px)", md: 1 }}
                minW={{ base: "calc(50% - 8px)", md: "auto" }}
                h={{ base: "80px", md: "100px" }}
                justify="center"
                position="relative"
                overflow="hidden"
                border="2px solid"
                borderColor={formData.plan === 'PREMIUM' ? "yellow.400" : "whiteAlpha.300"}
                sx={{
                  "&::before": formData.plan === 'PREMIUM' ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, #FFD700, #FFA500, #FF8C00, #FFD700)',
                    backgroundSize: '200% 200%',
                    animation: `${gradientAnimation} 3s ease infinite`,
                    opacity: 0.2,
                  } : {},
                  "&::after": formData.plan === 'PREMIUM' ? {
                    content: '""',
                    position: 'absolute',
                    inset: -2,
                    borderRadius: 'lg',
                    padding: '2px',
                    background: 'linear-gradient(45deg, #FFD700, #FFA500, #FF8C00, #FFD700)',
                    backgroundSize: '200% 200%',
                    animation: `${gradientAnimation} 3s ease infinite`,
                    WebkitMask: 
                      'linear-gradient(#fff 0 0) content-box, ' +
                      'linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  } : {}
                }}
              >
                <Tooltip 
                  hasArrow
                  label={
                    <VStack spacing={2} p={2}>
                      <Text fontWeight="bold">Premium Özellikleri:</Text>
                      {premiumFeatures.map((feature, index) => (
                        <HStack key={index} spacing={2}>
                          <Icon as={MdCheckCircle} color="green.300" />
                          <Text>{feature}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  }
                  placement="bottom"
                  bg="gray.700"
                  color="white"
                  borderRadius="md"
                  openDelay={200}
                >
                  <VStack spacing={2}>
                    <Icon 
                      as={formData.plan === 'PREMIUM' ? MdStars : MdInfo} 
                      color={formData.plan === 'PREMIUM' ? 'yellow.400' : 'white'} 
                      boxSize={6} 
                    />
                    <Text color="white" fontSize="sm" fontWeight="bold">
                      {formData.plan === 'PREMIUM' ? 'Premium' : 'Standart'}
                    </Text>
                    {formData.plan !== 'PREMIUM' && (
                      <Button
                        size="xs"
                        colorScheme="yellow"
                        variant="solid"
                        onClick={() => setIsUpgradeModalOpen(true)}
                      >
                        Yükselt
                      </Button>
                    )}
                  </VStack>
                </Tooltip>
              </VStack>
            </HStack>
          </HStack>

          <Divider />

          {/* Tabs */}
          <Tabs variant="soft-rounded" colorScheme="red">
            <Box overflowX="auto">
              <TabList minW="max-content" flexWrap={{ base: "nowrap", md: "wrap" }}>
                <Tab whiteSpace="nowrap" fontSize={{ base: "xs", md: "sm" }}>Genel Bilgiler</Tab>
                <Tab whiteSpace="nowrap" fontSize={{ base: "xs", md: "sm" }}>Adres Bilgileri</Tab>
                <Tab whiteSpace="nowrap" fontSize={{ base: "xs", md: "sm" }}>Çalışma Saatleri</Tab>
                <Tab whiteSpace="nowrap" fontSize={{ base: "xs", md: "sm" }}>Özellikler</Tab>
                <ManagerOnly>
                  <Tab whiteSpace="nowrap" fontSize={{ base: "xs", md: "sm" }}>Ödeme Bilgileri</Tab>
                </ManagerOnly>
              </TabList>
            </Box>

            <TabPanels>
              {/* İşletme Bilgileri Tab */}
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>İşletme Adı</FormLabel>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>Email</FormLabel>
                    <Input
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>Telefon</FormLabel>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>Açıklama</FormLabel>
                    <Input
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>Website</FormLabel>
                    <Input
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                    <FormHelperText fontSize={{ base: "xs", md: "sm" }}>Sosyal medya hesaplarınızı müşterilerinizle paylaşın</FormHelperText>
                  </FormControl>
                </SimpleGrid>
                
                <Box mt={6}>
                  <Heading size={{ base: "sm", md: "md" }} mb={4}>Sosyal Medya Hesapları</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                    <FormControl>
                      <FormLabel fontSize={{ base: "sm", md: "md" }}>
                        <HStack spacing={2}>
                          <Icon as={RiInstagramFill} color="#E4405F" boxSize={{ base: 4, md: 5 }} />
                          <Text>Instagram</Text>
                        </HStack>
                      </FormLabel>
                      <Input
                        name="socialMedia.instagram"
                        value={formData.socialMedia?.instagram || ''}
                        onChange={handleInputChange}
                        placeholder="@kullaniciadi veya tam URL"
                        isDisabled={isStaff}
                        size={{ base: "sm", md: "md" }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize={{ base: "sm", md: "md" }}>
                        <HStack spacing={2}>
                          <Icon as={RiFacebookFill} color="#1877F2" boxSize={{ base: 4, md: 5 }} />
                          <Text>Facebook</Text>
                        </HStack>
                      </FormLabel>
                      <Input
                        name="socialMedia.facebook"
                        value={formData.socialMedia?.facebook || ''}
                        onChange={handleInputChange}
                        placeholder="Kullanıcı adı veya tam URL"
                        isDisabled={isStaff}
                        size={{ base: "sm", md: "md" }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize={{ base: "sm", md: "md" }}>
                        <HStack spacing={2}>
                          <Icon as={RiTwitterXFill} color="#000000" boxSize={{ base: 4, md: 5 }} />
                          <Text>Twitter/X</Text>
                        </HStack>
                      </FormLabel>
                      <Input
                        name="socialMedia.twitter"
                        value={formData.socialMedia?.twitter || ''}
                        onChange={handleInputChange}
                        placeholder="@kullaniciadi veya tam URL"
                        isDisabled={isStaff}
                        size={{ base: "sm", md: "md" }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize={{ base: "sm", md: "md" }}>
                        <HStack spacing={2}>
                          <Icon as={RiLinkedinFill} color="#0077B5" boxSize={{ base: 4, md: 5 }} />
                          <Text>LinkedIn</Text>
                        </HStack>
                      </FormLabel>
                      <Input
                        name="socialMedia.linkedin"
                        value={formData.socialMedia?.linkedin || ''}
                        onChange={handleInputChange}
                        placeholder="Kullanıcı adı veya tam URL"
                        isDisabled={isStaff}
                        size={{ base: "sm", md: "md" }}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <Text mt={2} fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                    Tüm sosyal medya alanları opsiyoneldir. URL veya kullanıcı adı girebilirsiniz.
                  </Text>
                </Box>
              </TabPanel>

              {/* Adres Bilgileri Tab */}
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>Sokak</FormLabel>
                    <Input
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>Semt</FormLabel>
                    <Input
                      name="address.district"
                      value={formData.address.district}
                      onChange={handleInputChange}
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>Şehir</FormLabel>
                    <Input
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }}>Tam Adres</FormLabel>
                    <Input
                      name="address.fullAddress"
                      value={formData.address.fullAddress}
                      onChange={handleInputChange}
                      isDisabled={isStaff}
                      size={{ base: "sm", md: "md" }}
                    />
                  </FormControl>
                </SimpleGrid>
              </TabPanel>

              {/* Çalışma Saatleri Tab */}
              <TabPanel>
                <FormControl>
                <Card>
                  <Box overflowX="auto">
                    <TableContainer>
                      <Table variant="simple" minW={{ base: "500px", md: "auto" }}>
                      <Thead>
                        <Tr>
                          <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Gün</Th>
                          <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Durum</Th>
                          <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Açılış</Th>
                          <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>Kapanış</Th>
                          <Th fontSize={{ base: "xs", md: "sm" }} px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }} textAlign="center">İşlem</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {WORKING_HOURS_DAY_KEYS.map((day) => {
                          const hours = formData.workingHours?.[day] || DEFAULT_DAY_HOURS;
                          const dayNames = {
                            monday: 'Pazartesi',
                            tuesday: 'Salı',
                            wednesday: 'Çarşamba',
                            thursday: 'Perşembe',
                            friday: 'Cuma',
                            saturday: 'Cumartesi',
                            sunday: 'Pazar'
                          };

                          return (
                            <Tr key={day}>
                              <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                                <HStack spacing={{ base: 1, md: 2 }}>
                                  <Icon as={MdAccessTime} color="gray.500" boxSize={{ base: 4, md: 5 }} />
                                  <Text fontWeight="medium" fontSize={{ base: "xs", md: "sm" }}>{dayNames[day]}</Text>
                                </HStack>
                              </Td>
                              <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                                <HStack spacing={{ base: 1, md: 2 }}>
                                  <Icon 
                                    as={hours.isOpen ? MdCheckCircle : MdCancel} 
                                    color={hours.isOpen ? "green.500" : "red.500"}
                                    boxSize={{ base: 4, md: 5 }}
                                  />
                                  <Text color={hours.isOpen ? "green.500" : "red.500"} fontSize={{ base: "xs", md: "sm" }}>
                                    {hours.isOpen ? 'Açık' : 'Kapalı'}
                                  </Text>
                                </HStack>
                              </Td>
                              <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                                <Input
                                  type="time"
                                  size={{ base: "xs", md: "sm" }}
                                  name={`workingHours.${day}.open`}
                                  value={hours.open || ''}
                                  onChange={handleInputChange}
                                  isDisabled={!hours.isOpen || isStaff}
                                  bg={hours.isOpen ? "white" : "gray.100"}
                                  borderColor={hours.isOpen ? "gray.200" : "gray.100"}
                                />
                              </Td>
                              <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }}>
                                <Input
                                  type="time"
                                  size={{ base: "xs", md: "sm" }}
                                  name={`workingHours.${day}.close`}
                                  value={hours.close || ''}
                                  onChange={handleInputChange}
                                  isDisabled={!hours.isOpen || isStaff}
                                  bg={hours.isOpen ? "white" : "gray.100"}
                                  borderColor={hours.isOpen ? "gray.200" : "gray.100"}
                                />
                              </Td>
                              <Td px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }} textAlign="center">
                                <Switch
                                  id={`${day}-status`}
                                  isChecked={hours.isOpen}
                                  onChange={(e) => handleWorkingHoursToggle(day, e.target.checked)}
                                  colorScheme="green"
                                  size={{ base: "sm", md: "md" }}
                                  isDisabled={isStaff}
                                />
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  </Box>
                  <Box p={{ base: 3, md: 4 }}>
                    <Text textAlign="center" fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                      <Icon as={MdAccessTime} mr={2} boxSize={{ base: 4, md: 5 }} />
                      İşletmenizin çalışma saatlerini ayarlamak için açık/kapalı durumunu değiştirin ve saatleri belirleyin.
                    </Text>
                  </Box>
                </Card>
                </FormControl>
              </TabPanel>

              {/* Restoran Özellikleri Tab */}
              <TabPanel>
                <Card>
                  <Text fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={4}>
                    İşletmenizin özelliklerini seçin. Bu özellikler müşteriler tarafından görüntülenecektir.
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 3, md: 4 }}>
                    {[
                      { key: 'wifi', label: 'WiFi', icon: '📶' },
                      { key: 'parking', label: 'Otopark', icon: '🅿️' },
                      { key: 'terrace', label: 'Teras', icon: '🏞️' },
                      { key: 'seaView', label: 'Deniz Manzarası', icon: '🌊' },
                      { key: 'liveMusic', label: 'Canlı Müzik', icon: '🎵' },
                      { key: 'noSmoking', label: 'Sigara İçilmez', icon: '🚭' },
                      { key: 'accessible', label: 'Erişilebilir', icon: '♿' },
                      { key: 'buffet', label: 'Açık Büfe', icon: '🍽️' },
                      { key: 'breakfast', label: 'Kahvaltı Servisi', icon: '🥐' },
                      { key: 'openAfterMidnight', label: 'Gece Yarısı Sonrası', icon: '🌙' },
                      { key: 'kidsPlayground', label: 'Çocuk Parkı', icon: '🎠' },
                      { key: 'kidsTable', label: 'Çocuk Masası', icon: '🪑' },
                      { key: 'takeaway', label: 'Paket Servis', icon: '🥡' },
                      { key: 'outdoorSeating', label: 'Açık Hava', icon: '🌳' },
                      { key: 'petFriendly', label: 'Evcil Hayvan Dostu', icon: '🐾' },
                      { key: 'reservations', label: 'Rezervasyon', icon: '📅' },
                      { key: 'privateRoom', label: 'Özel Oda', icon: '🚪' },
                      { key: 'valetParking', label: 'Vale Park', icon: '🚗' },
                      { key: 'airConditioning', label: 'Klima', icon: '❄️' },
                      { key: 'heating', label: 'Isıtma', icon: '🔥' },
                    ].map((feature) => (
                      <FormControl key={feature.key} display="flex" alignItems="center">
                        <Box flex="1">
                          <HStack spacing={{ base: 2, md: 3 }}>
                            <Text fontSize={{ base: "lg", md: "xl" }}>{feature.icon}</Text>
                            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">
                              {feature.label}
                            </Text>
                          </HStack>
                        </Box>
                        <Switch
                          isChecked={formData.features?.[feature.key] || false}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              features: {
                                ...prev.features,
                                [feature.key]: e.target.checked
                              }
                            }));
                          }}
                          colorScheme="red"
                          size={{ base: "sm", md: "md" }}
                          isDisabled={isStaff}
                        />
                      </FormControl>
                    ))}
                  </SimpleGrid>
                </Card>
              </TabPanel>

              {/* Ödeme Bilgileri Tab - Sadece Manager */}
              <ManagerOnly>
                <TabPanel>
                <Card position="relative">
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg={useColorModeValue("rgba(255, 255, 255, 0.7)", "rgba(26, 32, 44, 0.7)")}
                    backdropFilter="blur(4px)"
                    zIndex={10}
                    borderRadius="lg"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    p={8}
                  >
                    <Icon as={MdLock} boxSize={16} color="gray.400" mb={4} />
                    <HStack spacing={2} mb={2}>
                      <Icon as={MdSchedule} color="orange.500" />
                      <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        Yakında Eklenecek
                      </Text>
                    </HStack>
                    <Text color={textColorSecondary} textAlign="center" maxW="400px">
                      Ödeme bilgileri özelliği yakında aktif edilecektir. Bu özellik sayesinde kart bilgilerinizi kaydedebilecek ve otomatik ödeme yapabileceksiniz.
                    </Text>
                  </Box>
                  
                  <Box opacity={0.5} pointerEvents="none">
                  <Heading 
                    size="sm" 
                    mb={6}
                    color={textColor}
                    fontWeight="600"
                    fontSize="16px"
                    pb={2}
                    borderBottom="2px solid"
                    borderColor={brandStars}
                  >
                    <HStack>
                      <Icon as={MdCreditCard} />
                      <Text>Ödeme Kartı Bilgileri</Text>
                    </HStack>
                  </Heading>

                  {isLoadingCardStatus ? (
                    <Text color={textColorSecondary}>Yükleniyor...</Text>
                  ) : (
                    <>
                      {cardStatus?.isCardSaved ? (
                        <Box
                          p={6}
                          borderRadius="lg"
                          bg={hoverBg}
                          border="2px solid"
                          borderColor="green.500"
                          mb={6}
                        >
                          <HStack justify="space-between" mb={4}>
                            <VStack align="start" spacing={2}>
                              <Text color="gray.500" fontSize="sm" fontWeight="500">
                                Kayıtlı Kart
                              </Text>
                              <Text color={textColor} fontSize="xl" fontWeight="700">
                                •••• {cardStatus.cardLastFour}
                              </Text>
                              <Text color="gray.600" fontSize="sm">
                                {cardStatus.cardBrand}
                              </Text>
                            </VStack>
                            <Icon as={MdCheckCircle} color="green.500" boxSize={8} />
                          </HStack>
                          <Text color="gray.600" fontSize="xs">
                            Bu kart aylık ücret ve komisyon farkı tahsilatı için kullanılacaktır.
                          </Text>
                        </Box>
                      ) : (
                        <Box
                          p={6}
                          borderRadius="lg"
                          bg="orange.50"
                          border="2px solid"
                          borderColor="orange.300"
                          mb={6}
                        >
                          <HStack mb={2}>
                            <Icon as={MdInfo} color="orange.500" />
                            <Text color="orange.700" fontWeight="600">
                              Kart Kayıtlı Değil
                            </Text>
                          </HStack>
                          <Text color="orange.600" fontSize="sm">
                            Aylık ücret ve komisyon farkı tahsilatı için kart bilgilerinizi kaydetmeniz gerekmektedir.
                          </Text>
                        </Box>
                      )}

                      <Heading 
                        size="sm" 
                        mb={4}
                        color={textColor}
                        fontWeight="600"
                        fontSize="14px"
                      >
                        {cardStatus?.isCardSaved ? 'Kartı Güncelle' : 'Yeni Kart Ekle'}
                      </Heading>

                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel>Kart Numarası</FormLabel>
                          <Input
                            value={cardFormData.cardNumber}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
                              const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                              setCardFormData(prev => ({ ...prev, cardNumber: formatted }));
                            }}
                            placeholder="1234 5678 9012 3456"
                            maxLength={19}
                            bg={inputBg}
                            borderColor={borderColor}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Kart Sahibi Adı</FormLabel>
                          <Input
                            value={cardFormData.cardHolderName}
                            onChange={(e) => setCardFormData(prev => ({ ...prev, cardHolderName: e.target.value }))}
                            placeholder="AD SOYAD"
                            bg={inputBg}
                            borderColor={borderColor}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Son Kullanma Tarihi</FormLabel>
                          <Flex gap={2}>
                            <Select
                              value={cardFormData.expiryMonth}
                              onChange={(e) => setCardFormData(prev => ({ ...prev, expiryMonth: e.target.value }))}
                              placeholder="Ay"
                              bg={inputBg}
                              borderColor={borderColor}
                            >
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                <option key={month} value={month.toString().padStart(2, '0')}>
                                  {month.toString().padStart(2, '0')}
                                </option>
                              ))}
                            </Select>
                            <Select
                              value={cardFormData.expiryYear}
                              onChange={(e) => setCardFormData(prev => ({ ...prev, expiryYear: e.target.value }))}
                              placeholder="Yıl"
                              bg={inputBg}
                              borderColor={borderColor}
                            >
                              {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() + i).map(year => (
                                <option key={year} value={year.toString().slice(-2)}>
                                  {year}
                                </option>
                              ))}
                            </Select>
                          </Flex>
                        </FormControl>

                        <FormControl>
                          <FormLabel>CVV</FormLabel>
                          <Input
                            value={cardFormData.cvv}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                              setCardFormData(prev => ({ ...prev, cvv: value }));
                            }}
                            placeholder="123"
                            maxLength={3}
                            type="password"
                            bg={inputBg}
                            borderColor={borderColor}
                          />
                        </FormControl>
                      </SimpleGrid>

                      <Button
                        mt={6}
                        colorScheme="red"
                        onClick={handleCardSave}
                        isLoading={isSavingCard}
                        leftIcon={<MdCreditCard />}
                      >
                        {cardStatus?.isCardSaved ? 'Kartı Güncelle' : 'Kartı Kaydet'}
                      </Button>
                    </>
                  )}
                  </Box>
                </Card>
                </TabPanel>
              </ManagerOnly>
            </TabPanels>
          </Tabs>

          <ManagerOnly>
            <Button
              color="white"
              bg="#E45858"
              size="lg"
              onClick={handleSubmit}
              w={{ base: "100%", md: "auto" }}
              alignSelf="end"
            >
              Değişiklikleri Kaydet
            </Button>
          </ManagerOnly>
          
          {isStaff && (
            <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200" mt={4}>
              <Text color="blue.700" fontSize="sm" textAlign="center">
                ⓘ Bu sayfada sadece bilgileri görüntüleyebilirsiniz. Değişiklik yapmak için yönetici yetkisine ihtiyacınız var.
              </Text>
            </Box>
          )}
        </VStack>
      </Card>
      
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onConfirm={async () => {
          // Plan bilgisini güncelle
          const pricingInfo = await pricingService.getPricingInfo();
          if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
            setFormData(prev => ({
              ...prev,
              plan: pricingInfo.data.business.plan
            }));
          }
          // Restaurant detaylarını yeniden yükle
          await fetchRestaurantDetails();
        }}
      />
    </Box>
  );
} 